import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { map, Observable, of, switchMap, take } from 'rxjs';

import { UserStateModel } from '@hmax-order/frontend/state';
import { UserService } from '../../user/user.service';
import { AuthService } from '../auth.service';

@Injectable({ providedIn: 'root' })
export class RoleGuard implements CanActivate {
  private _userService = inject(UserService);
  private _authService = inject(AuthService);
  private _router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const requiredRoles: string[] = route.data['roles'] || [];

    // Revalida a EXISTÊNCIA da sessão antes de ler os papéis. Sob o aninhamento
    // atual (RoleGuard sempre sob AuthGuard em `canActivateChild`), `check()` já
    // resolveu e devolve `of(true)` de graça. Fora desse ancestral, sem esta
    // revalidação o guard serviria papéis de um snapshot do localStorage com token
    // expirado — escalonamento de privilégio. Não refaz o fetch de papéis com a
    // sessão já viva (limitação de JWT+snapshot, igual ao AuthGuard): garante
    // sessão válida antes de decidir acesso, não frescor de papel.
    // ⚠️ Não colocar RoleGuard e AuthGuard no MESMO array de `canActivate`: rodariam
    // concorrentes com `_authenticated=false` e disparariam dois `login-by-token`.
    return this._authService.check().pipe(
      switchMap((authenticated) => {
        if (!authenticated) {
          const redirectURL = state.url === '/sign-out' ? '' : `redirectURL=${state.url}`;
          return of(this._router.parseUrl(`sign-in?${redirectURL}`));
        }

        return this._userService.get().pipe(
          take(1),
          map((user: UserStateModel) => {
            const userRoles: string[] = user?.roles || [];
            const hasAccess = requiredRoles.some((role) => userRoles.includes(role));

            // Redirect declarativo (UrlTree), como o ramo não-autenticado — evita
            // um `navigate` imperativo competindo com o resultado do guard.
            return hasAccess ? true : this._router.parseUrl('/');
          }),
        );
      }),
    );
  }
}
